import React, { useEffect, useState } from 'react';
import { Button, CircularProgressLoader } from '../../../components';
import { EnterViceService } from '../../../services';
import type { PresetListItem } from '../../../types/entervice';
import StatusChip from './StatusChip';
import { describeError } from './errors';
import '../../Modal/Modal.module.scss';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

const byRank = (a: PresetListItem, b: PresetListItem) => {
  const ra = a.rank ?? Number.MAX_SAFE_INTEGER;
  const rb = b.rank ?? Number.MAX_SAFE_INTEGER;
  return ra === rb ? a.name.localeCompare(b.name) : ra - rb;
};

/**
 * Modal body: reorder the ranked presets (active + inactive; drafts get a rank on first activation)
 * with up/down arrows and persist the 1..n order through `PUT /presets/ranks`.
 */
const RankEditor: React.FC<Props> = ({ onClose, onSaved }) => {
  const [items, setItems] = useState<PresetListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    EnterViceService.listPresets({ page: 1, limit: 100 })
      .then((page) => {
        if (cancelled) return;
        setItems(page.data.filter((p) => p.status === 'active' || p.status === 'inactive').sort(byRank));
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e, 'Failed to load presets.').message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const save = () => {
    setSaving(true);
    setError('');
    EnterViceService.setPresetRanks(items.map((p, index) => ({ presetId: p._id, rank: index + 1 })))
      .then(() => onSaved())
      .catch((e) => {
        setError(describeError(e, 'Failed to save ranks.').message);
        setSaving(false);
      });
  };

  return (
    <>
      <h2>Edit Ranks</h2>
      <p>Order presets as they appear in the app. Drafts join the list when they are first activated.</p>
      {loading ? (
        <div className="savingLoader"><CircularProgressLoader /></div>
      ) : items.length === 0 ? (
        <div className="ev-note">No ranked presets yet. Activate a preset to give it a rank.</div>
      ) : (
        <div className="ev-rank-list">
          {items.map((preset, index) => (
            <div className="ev-rank-row" key={preset._id}>
              <span className="ev-rank-no">{index + 1}</span>
              {preset.thumbnailUrl
                ? <img className="ev-thumb" src={preset.thumbnailUrl} alt="" />
                : <div className="ev-thumb ev-thumb--empty">none</div>}
              <div className="ev-cell-name">
                <span className="ev-name">{preset.name}</span>
                <span className="ev-key">{preset.key} · <StatusChip status={preset.status} /></span>
              </div>
              <div className="ev-rank-btns">
                <button type="button" className="ev-mini" title="Move up" onClick={() => move(index, -1)} disabled={index === 0 || saving}>↑</button>
                <button type="button" className="ev-mini" title="Move down" onClick={() => move(index, 1)} disabled={index === items.length - 1 || saving}>↓</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className="ev-error">{error}</div>}
      <div className="modalButtonWrapper">
        <Button buttonType="button" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button buttonType="button" onClick={save} disabled={saving || loading || items.length === 0}>
          {saving ? 'Saving…' : 'Save order'}
        </Button>
      </div>
    </>
  );
};

export default RankEditor;
